import { formatMetricValue, type ClientStatusDto } from '@citilight-monitor/contracts';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { HEALTH_CHIP, HEALTH_LABELS, metricStatus } from '../../lib/metrics';
import { formatDateTime, formatRelative } from '../../lib/time';
import { Meter, StatusChip } from '../atoms';

const RESOURCES = [
  { key: 'cpu_percent', label: 'CPU' },
  { key: 'memory_percent', label: 'Memory' },
  { key: 'disk_percent', label: 'Disk' },
] as const;

const EDGE = {
  critical: 'border-chip-critical-fg/30',
  serious: 'border-chip-serious-fg/30',
  warning: 'border-chip-warning-fg/25',
  neutral: 'border-line',
  good: 'border-line',
} as const;

interface ClientCardsProps {
  clients: ClientStatusDto[];
}

/** One card per client: state and its resources against their limits. */
export function ClientCards({ clients }: ClientCardsProps) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {clients.map((status) => (
        <ClientCard key={status.client.id} status={status} />
      ))}
    </div>
  );
}

function ClientCard({ status }: { status: ClientStatusDto }) {
  const { client, latest, health } = status;
  const chip = HEALTH_CHIP[health];
  const resources = RESOURCES.filter((r) => client.metrics.some((m) => m.key === r.key));
  const appMetrics = client.metrics.filter((m) => m.source === 'app' && m.unit !== 'state');

  return (
    <Link
      to={`/clients/${client.id}`}
      className={cn(
        'group flex min-w-0 flex-col gap-6 rounded-xl border bg-surface p-7 text-inherit no-underline transition-[box-shadow,border-color] hover:border-accent/40 hover:shadow-raised',
        EDGE[chip.tone],
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-display text-2xl font-bold tracking-[-0.01em] group-hover:text-accent">{client.name}</div>
          <div className="truncate font-mono text-sm text-fg-muted" title={latest ? formatDateTime(latest.timestamp) : undefined}>
            {client.id} · {latest ? formatRelative(latest.timestamp) : 'never polled'}
          </div>
        </div>
        <StatusChip tone={chip.tone} label={HEALTH_LABELS[health].label} />
      </div>

      {resources.length > 0 ? (
        <div className="grid grid-cols-3 gap-6">
          {resources.map((r) => {
            const value = latest?.metrics[r.key];
            const rule = metricStatus(status, r.key);
            return (
              <div key={r.key} className="flex min-w-0 flex-col gap-2">
                <div className="flex items-baseline justify-between gap-1 text-sm">
                  <span className="text-fg-muted">{r.label}</span>
                  <span className="font-mono font-medium">{formatMetricValue('percent', value)}</span>
                </div>
                <Meter percent={value ?? 0} threshold={rule?.rule.threshold} tone={rule?.tone} className="h-2.5" />
              </div>
            );
          })}
        </div>
      ) : (
        appMetrics.length > 0 && (
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {appMetrics.map((m) => (
              <span key={m.key} className="text-fg-muted">
                {m.label} <span className="font-mono font-medium text-fg">{formatMetricValue(m.unit, latest?.metrics[m.key])}</span>
              </span>
            ))}
          </div>
        )
      )}
    </Link>
  );
}
